import React, { useState } from "react";
import { DeleteFilled, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Modal,
  Table,
  Upload,
  message,
  Row,
  Col,
  Select,
} from "antd";
import {
  useGetServicesQuery,
  useAddServiceMutation,
  useDeleteServiceMutation,
  primaryBaseUrl,
  useGetTripTypesQuery,
} from "../../../redux/Service/api";
import { ExclamationCircleFilled } from "@ant-design/icons";
import Heading from "../../../Components/Heading/Heading";
import ServerError from "../../../Components/utils/ServerError";

const { confirm } = Modal;
const { Option } = Select;

const Services: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [modalKey, setModalKey] = useState(0);
  const [form] = Form.useForm();
  const [file, setFile] = useState<any>(null);

  const [addService, { isLoading }] = useAddServiceMutation();
  const [deleteService, { isLoading: delIsLoading }] =
    useDeleteServiceMutation();

  const {
    data: servicesData,
    isError: isServicesError,
    isLoading: isServicesLoading,
  } = useGetServicesQuery(undefined);

  const services: any = servicesData?.ResponseObject ?? [];

  const {
    data: tripTypesData,
    isError: isTripTypesError,
    isLoading: isTripTypesLoading,
  } = useGetTripTypesQuery(undefined);

  const tripTypes: any = tripTypesData?.ResponseObject ?? [];

  const handleAddService = async (values: any) => {
    const formData = new FormData();
    formData.append("ServiceImageName", file);
    formData.append("ServiceName", values.ServiceName);
    formData.append("ServiceTypeId", String(values.ServiceTypeId));
    formData.append("Price", String(values.Price));
    formData.append("TaxRate", String(values.TaxRate));
    formData.append("IsPerDay", String(values.IsPerDay));
    formData.append("IsDiffSalesPurch", String(values.IsDiffSalesPurch));
    formData.append("IsTotalInvoice", String(values.IsTotalInvoice));

    try {
      await addService(formData);
      message.success("تمت إضافة الخدمة بنجاح");
      form.resetFields();
      setModalKey((prev) => prev + 1);
      setOpenModal(false);
    } catch (error) {
      console.error("خطأ أثناء إضافة الخدمة:", error);
      message.error("فشل في إضافة الخدمة. يرجى المحاولة مرة أخرى.");
    }
  };

  const handleDeleteService = (id: number) => {
    confirm({
      title: "هل أنت متأكد أنك تريد حذف هذه الخدمة؟",
      icon: <ExclamationCircleFilled />,
      content: "بالضغط على موافق سيتم حذف الخدمة بشكل دائم.",
      async onOk() {
        try {
          await deleteService(id);
          message.success("تم حذف الخدمة بنجاح");
        } catch (error) {
          console.error(error);
          message.error("فشل في حذف الخدمة.");
        }
      },
    });
  };

  const columns = [
    {
      title: "رقم الخدمة",
      dataIndex: "ServiceID",
      key: "ServiceID",
    },
    {
      title: "الصورة",
      dataIndex: "ServiceImageName",
      key: "ServiceImageName",
      render: (image: string) =>
        image ? (
          <img
            src={primaryBaseUrl + image}
            alt="Service"
            className="w-20 h-20 object-cover"
          />
        ) : (
          "لا توجد صورة"
        ),
    },
    {
      title: "اسم الخدمة",
      dataIndex: "ServiceName",
      key: "ServiceName",
    },
    {
      title: "السعر",
      dataIndex: "Price",
      key: "Price",
      render: (price: number) => `${price.toFixed(2)} جنيه`,
    },
    {
      title: "الضريبة",
      dataIndex: "TaxRate",
      key: "TaxRate",
      render: (taxRate: number) => `${taxRate}%`,
    },
    {
      title: "نوع سعر الخدمة",
      dataIndex: "IsPerDay",
      key: "IsPerDay",
      render: (IsPerDay: number) => `${IsPerDay ? "باليوم" : "بالخدمة"}`,
    },
    {
      title: "الإجراءات",
      key: "actions",
      render: (_: any, service: any) => (
        <div className="flex gap-2">
          <Button
            type="text"
            icon={<DeleteFilled />}
            danger
            onClick={() => handleDeleteService(service.ServiceID)}
          />
        </div>
      ),
    },
  ];

  const handleFileChange = (fileList: any) => {
    if (fileList.length > 0) {
      setFile(fileList[0].originFileObj);
    } else {
      setFile(null);
    }
  };

  return (
    <section className="p-4 section_padding mt-0 pt-0 relative">
      <Heading heading="الخدمات" />

      {isServicesError && <ServerError />}

      <div className="text-center my-3">
        <Button
          className="w-[150px]"
          type="primary"
          onClick={() => setOpenModal(true)}
        >
          إضافة خدمة
        </Button>
      </div>

      <div className="mx-auto">
        <Table
          columns={columns}
          dataSource={services}
          rowKey="ServiceID"
          loading={isServicesLoading || delIsLoading}
          pagination={{ pageSize: 5 }}
        />
      </div>

      <Modal
        title="إضافة خدمة"
        centered
        open={openModal}
        onCancel={() => setOpenModal(false)}
        footer={null}
        key={modalKey}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddService}
          initialValues={{ IsPerDay: false }}
        >
          <Form.Item
            label="اسم الخدمة"
            name="ServiceName"
            rules={[{ required: true, message: "يرجى إدخال اسم الخدمة" }]}
          >
            <Input size="large" placeholder="أدخل اسم الخدمة" />
          </Form.Item>

          <Form.Item
            label="نوع الخدمة"
            name="ServiceTypeId"
            rules={[{ required: true, message: "يرجى اختيار نوع الخدمة" }]}
          >
            <Select size="large" placeholder="اختر نوع الخدمة">
              {tripTypes?.map((tripType: any) => (
                <Option
                  key={tripType.ServiceTypeID}
                  value={tripType.ServiceTypeID}
                >
                  {tripType.ServiceTypeName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="السعر"
                name="Price"
                rules={[{ required: true, message: "يرجى إدخال السعر" }]}
              >
                <Input size="large" type="number" placeholder="أدخل السعر" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="نسبة الضريبة"
                name="TaxRate"
                rules={[{ required: true, message: "يرجى إدخال نسبة الضريبة" }]}
              >
                <Input
                  size="large"
                  type="number"
                  placeholder="أدخل نسبة الضريبة"
                />
              </Form.Item>
            </Col>
          </Row>

          <Upload
            className="mb-2"
            customRequest={({ file, onSuccess }) => {
              onSuccess?.("ok");
            }}
            onChange={({ fileList }) => handleFileChange(fileList)}
            listType="picture"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>صورة الخدمة (بحد أقصى: 1)</Button>
          </Upload>

          <Form.Item
            label="هل الحجز باليوم؟"
            name="IsPerDay"
            valuePropName="checked"
          >
            <Select size="large" placeholder="هل الحجز باليوم؟">
              <Option value={true}>نعم</Option>
              <Option value={false}>لا</Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="هل يوجد فرق بين البيع والشراء؟"
            name="IsDiffSalesPurch"
            valuePropName="checked"
          >
            <Select size="large" placeholder="هل يوجد فرق بين البيع والشراء؟">
              <Option value={true}>نعم</Option>
              <Option value={false}>لا</Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="فوترة؟"
            name="IsTotalInvoice"
            valuePropName="checked"
          >
            <Select size="large" placeholder="فوترة؟">
              <Option value={true}>نعم</Option>
              <Option value={false}>لا</Option>
            </Select>
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={isLoading}>
            إضافة
          </Button>
        </Form>
      </Modal>
    </section>
  );
};

export default Services;
